import { useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
} from "lucide-react";
import { Hero } from "@/components/Hero";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";
import { IMG, VIDEO, POSTER } from "@/lib/images";
import { INDUSTRIES } from "@/data/industries";
import { getJSON } from "@/lib/api";
import { VideoTile } from "@/components/VideoTile";
import { SegmentedPillToggle } from "@/components/SegmentedPillToggle";
import { ReelBand } from "@/components/ReelBand";
import { MarketingDNAAudit } from "@/components/MarketingDNAAudit";
import { WhatYouGet } from "@/components/WhatYouGet";
import { BackOffice } from "@/components/BackOffice";
import { WhyUs } from "@/components/WhyUs";
import { Guarantees } from "@/components/Guarantees";

const Label = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ember)]">
    {children}
  </span>
);

const CREATIVE_PRINCIPLES = [
  { n: "01", head: "Direction first, AI second.", body: "Every frame is on paper before it's in a prompt. Script, board, and shot list lock the vision." },
  { n: "02", head: "Consistency by design.", body: "Character, brand, and visual language are pinned at the concept stage. The model doesn't surprise us shot-to-shot." },
  { n: "03", head: "Film grammar, AI tempo.", body: "The craft of a film, the pace of modern advertising. Speed without losing what the work actually needs." },
];

const leader = {
  name: "Daniyal Syed",
  title: "Growth Team lead",
  photo: `${process.env.PUBLIC_URL}/team/daniyal-syed.jpg`,
  bio: "A senior growth marketer who's run paid, lifecycle, outreach, and creative across verticals. He directs the Growth Team's agents, reviews the output, and answers for every number.",
};

export default function Growth() {
  const { data: projects = [] } = useQuery({
    queryKey: ["projects", "All"],
    queryFn: () => getJSON("/projects"),
  });
  const inTeam = (p, name) => (p.teams && p.teams.length ? p.teams : p.team ? [p.team] : []).includes(name);
  const portfolio = projects.filter((p) => inTeam(p, "Marketing & Creative")).slice(0, 8);

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "creative" ? "creative" : "marketing";
  const toggleRef = useRef(null);

  const setTab = useCallback(
    (t) => {
      setSearchParams(t === "marketing" ? {} : { tab: "creative" });
      requestAnimationFrame(() =>
        window.scrollTo({ top: 0, behavior: "smooth" }),
      );
    },
    [setSearchParams],
  );

  return (
    <>
      {/* 1. Hero — SHARED (background swaps per active tab) */}
      <Hero
        key={tab}
        image={tab === "creative" ? POSTER.creative : POSTER.growth}
        video={tab === "creative" ? VIDEO.creative : VIDEO.growth}
        eyebrow="THE GROWTH TEAM"
        title={tab === "creative" ? "The team that gets you attention." : "The team that gets you customers."}
        subline="Marketing strategy and creative built on our no-guessing framework — validated against what's already proven, never hunches."
      />

      {/* Sticky segmented toggle — sits where the hero CTA was, pins on scroll */}
      <div className="pointer-events-none sticky top-[84px] z-40 -mt-24 mb-6 md:top-[88px]">
        <div className="mx-auto flex max-w-7xl justify-center px-6 md:justify-start md:px-12">
          <div ref={toggleRef}>
            <SegmentedPillToggle
              className="pointer-events-auto"
              ariaLabel="Growth focus"
              value={tab}
              onChange={setTab}
              idPrefix="tab"
              controlsPrefix="panel"
              testId="growth-toggle"
              options={[
                { value: "marketing", label: "Marketing", testId: "growth-tab-marketing" },
                { value: "creative", label: "Creative", testId: "growth-tab-creative" },
              ]}
            />
          </div>
        </div>
      </div>

      {/* ── MARKETING PANEL ── */}
      <div role="tabpanel" id="panel-marketing" aria-labelledby="tab-marketing" hidden={tab !== "marketing"} data-testid="growth-panel-marketing">
        <div key={`m-${tab}`} style={{ animation: tab === "marketing" ? "demoModalFade .45s var(--ease)" : undefined }}>
          {/* Marketing DNA Audit */}
          <MarketingDNAAudit />

          {/* What you get */}
          <WhatYouGet />

          {/* The back office — eight-agent production line */}
          <BackOffice />

          {/* Why us */}
          <WhyUs />

          {/* Industries handoff */}
          <section className="bg-[var(--bg-contrast)] py-28 md:py-36" data-testid="growth-industries">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
              <div className="max-w-2xl">
                <Reveal><Label>Built for your market</Label></Reveal>
                <Reveal delay={80}>
                  <h2 className="mt-7 t-section">Built differently for every industry.</h2>
                </Reveal>
              </div>
              <div className="mt-14 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-5">
                {Object.values(INDUSTRIES).map((ind, i) => (
                  <Reveal key={ind.route} delay={i * 80}>
                    <Link to={ind.route} className="group relative block aspect-[3/4] overflow-hidden rounded-2xl bg-[#141210]" data-testid={`growth-industry-${ind.route.slice(1)}`}>
                      <img src={ind.heroImage} alt={ind.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-5">
                        <p className="text-base font-semibold text-white">{ind.name}</p>
                        <p className="mt-1 text-[12px] text-white/70">See your industry's agent roster</p>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* Guarantees — pending Kathryn legal sign-off before publish */}
          <Guarantees />
        </div>
      </div>

      {/* ── CREATIVE PANEL ── */}
      <div role="tabpanel" id="panel-creative" aria-labelledby="tab-creative" hidden={tab !== "creative"} data-testid="growth-panel-creative">
        <div key={`c-${tab}`} style={{ animation: tab === "creative" ? "demoModalFade .45s var(--ease)" : undefined }}>
          {/* Block 1 — How we think */}
          <section className="bg-[var(--charcoal)] py-28 text-white md:py-36" data-testid="growth-creative-think">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
              <div className="max-w-2xl">
                <Reveal>
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--mint)]">Creative</span>
                </Reveal>
                <Reveal delay={80}>
                  <h2 className="mt-7 t-section text-white">How we think.</h2>
                </Reveal>
                <Reveal delay={140}>
                  <p className="mt-5 text-base leading-relaxed text-white/70 sm:text-lg">
                    The craft comes first. The tools just keep up.
                  </p>
                </Reveal>
              </div>
              <div className="mt-16 grid gap-6 md:grid-cols-3">
                {CREATIVE_PRINCIPLES.map((p, i) => (
                  <Reveal key={p.n} delay={i * 100}>
                    <div className="flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-7">
                      <span className="text-3xl font-semibold tracking-[-0.03em] text-[var(--mint)]">{p.n}</span>
                      <h3 className="mt-5 text-xl font-semibold tracking-[-0.02em] text-white">{p.head}</h3>
                      <p className="mt-3 text-[15px] leading-relaxed text-white/60">{p.body}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* Block 2 — The work (masonry grid, matching /work) */}
          {portfolio.length > 0 && (
            <section className="bg-[var(--charcoal)] pb-28 text-white md:pb-36" data-testid="growth-portfolio">
              <div className="mx-auto max-w-7xl px-6 md:px-12">
                <div className="flex flex-wrap items-end justify-between gap-6">
                  <div className="max-w-2xl">
                    <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--mint)]">Selected work</span>
                    <Reveal delay={80}>
                      <h2 className="mt-7 t-section text-white">Work that doesn't look AI-made.</h2>
                    </Reveal>
                  </div>
                  <Reveal delay={140}>
                    <Link to="/gallery" className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--mint)] hover:underline" data-testid="growth-portfolio-link">
                      See the full portfolio <ArrowRight size={15} />
                    </Link>
                  </Reveal>
                </div>
                <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
                  {portfolio.map((p, i) => (
                    <VideoTile
                      key={p.slug}
                      project={p}
                      delay={(i % 4) * 90}
                      aspect={p.featured ? "aspect-video" : "aspect-[4/5]"}
                      className={p.featured ? "md:col-span-2" : ""}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
      </div>

      {/* Human leadership — SHARED (below panels) */}
      <section className="bg-[var(--bg-contrast)] py-28 md:py-36" data-testid="growth-leader">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 md:px-12 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-2xl border border-black/5">
              <img src={leader.photo} alt={leader.name} className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
          <div>
            <Reveal><Label>Human leadership</Label></Reveal>
            <Reveal delay={80}>
              <h2 className="mt-7 t-section">{leader.name}</h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-2 text-sm font-semibold uppercase tracking-[0.1em] text-[var(--muted)]">
                {leader.title} · directs strategy, reviews output, signs off before anything ships
              </p>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-4 text-base leading-relaxed text-[var(--muted)]">{leader.bio}</p>
            </Reveal>
            <Reveal delay={260}>
              <div className="mt-8 flex items-start gap-3 rounded-2xl border border-[var(--ember)]/30 bg-[var(--ember)]/[0.05] p-6">
                <ShieldCheck size={20} className="mt-0.5 flex-shrink-0 text-[var(--ember)]" />
                <p className="text-[15px] leading-relaxed text-[var(--ink)]">
                  The AI executes at scale. The human expert makes sure what it's
                  executing is actually worth sending. That oversight is included
                  at every tier — that's AI Standards.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Reel feature band — drives to the Video Gallery */}
      <ReelBand />

      {/* CTA band — SHARED */}
      <CtaBand />
    </>
  );
}
